import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { framesService } from '../services/framesService';
import { invalidateFrameStatusQueries } from '@/shared/utils/queryUtils';
import type {
  CreateFrameInput,
  UpdateFrameInput,
  FrameFilters,
} from '@/shared/types/domain.types';

/**
 * Query key factory for frames
 */
export const framesKeys = {
  all: ['frames'] as const,
  lists: () => [...framesKeys.all, 'list'] as const,
  list: (filters?: FrameFilters) => [...framesKeys.lists(), filters] as const,
  details: () => [...framesKeys.all, 'detail'] as const,
  detail: (id: string) => [...framesKeys.details(), id] as const,
};

/**
 * Hook to fetch frames with filters
 */
export function useFrames(filters?: FrameFilters) {
  return useQuery({
    queryKey: framesKeys.list(filters),
    queryFn: () => framesService.getFrames(filters),
  });
}

/**
 * Hook to fetch a single frame by ID
 */
export function useFrame(id: string) {
  return useQuery({
    queryKey: framesKeys.detail(id),
    queryFn: () => framesService.getFrameById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a new frame
 */
export function useCreateFrame() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateFrameInput) => framesService.createFrame(input),
    onSuccess: () => {
      // Use centralized invalidation to update all frame-status-dependent views
      invalidateFrameStatusQueries(queryClient);
    },
  });
}

/**
 * Hook to update an existing frame
 */
export function useUpdateFrame() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateFrameInput) => framesService.updateFrame(input),
    onSuccess: (data) => {
      // Invalidate specific frame detail
      queryClient.invalidateQueries({ queryKey: framesKeys.detail(data.id) });
      // Use centralized invalidation to update all frame-status-dependent views
      invalidateFrameStatusQueries(queryClient);
    },
  });
}

/**
 * Hook to delete a frame
 */
export function useDeleteFrame() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => framesService.deleteFrame(id),
    onSuccess: () => {
      // Use centralized invalidation to update all frame-status-dependent views
      invalidateFrameStatusQueries(queryClient);
    },
  });
}
